/**
 * BusinessContextEditor — inline panel for the LLM business-context blob.
 *
 * Not a dialog: renders directly in the per-connection management
 * surface. Three actions:
 *   - "Salva"         → PUT /business-context (dirty-tracked, primary CTA)
 *   - "Rigenera con AI" → POST /regenerate-context (secondary, 10-30s
 *                         disabled while pending)
 *   - "Annulla"        → ghost, reverts local buffer to server snapshot
 *
 * Italian copy, no English leaks. The textarea is the single ARIA
 * labelled input with ``aria-describedby`` pointing at the timestamp
 * helper.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, Sparkles } from "lucide-react";
import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Skeleton,
} from "@ando/ui";

import {
  useBusinessContextQuery,
  useRegenerateBusinessContext,
  useUpdateBusinessContext,
} from "../../hooks/useBusinessContext";
import { InlineErrorBanner } from "../InlineErrorBanner";

export interface BusinessContextEditorProps {
  readonly connId: string;
}

function formatTimestamp(epochMs: number | null): string | null {
  if (epochMs === null) return null;
  const d = new Date(epochMs);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const BusinessContextEditor: React.FC<BusinessContextEditorProps> = ({ connId }) => {
  const { i18n } = useLingui();
  const query = useBusinessContextQuery(connId);
  const updateMutation = useUpdateBusinessContext();
  const regenerateMutation = useRegenerateBusinessContext();

  const serverText = query.data?.business_context ?? "";
  const [draft, setDraft] = React.useState(serverText);
  const [savedAt, setSavedAt] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmRegenerate, setConfirmRegenerate] = React.useState(false);

  // Sync draft when the server snapshot loads / refreshes. We track the
  // last server snapshot in a ref to decide whether to clobber the
  // local buffer (only on server-side change, never on a keystroke).
  const isDirty = draft !== serverText;
  const lastSyncedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (lastSyncedRef.current !== serverText) {
      lastSyncedRef.current = serverText;
      setDraft(serverText);
    }
  }, [serverText]);

  const handleSave = React.useCallback(() => {
    setError(null);
    updateMutation.mutate(
      { connId, businessContext: draft },
      {
        onSuccess: () => setSavedAt(Date.now()),
        onError: (err) =>
          setError(err instanceof Error ? err.message : i18n._(msg`Salvataggio non riuscito.`)),
      },
    );
  }, [connId, draft, updateMutation, i18n]);

  const handleRegenerate = React.useCallback(() => {
    setError(null);
    // ``force``: the person just confirmed in this editor that the current
    // text — possibly hand-written and locked on the BE — must be replaced.
    regenerateMutation.mutate(
      { connId, force: true },
      {
        onSuccess: (data) => {
          setDraft(data.business_context);
          setSavedAt(Date.now());
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : i18n._(msg`Rigenerazione non riuscita.`)),
      },
    );
  }, [connId, regenerateMutation, i18n]);

  const handleCancel = React.useCallback(() => {
    setDraft(serverText);
    setError(null);
  }, [serverText]);

  const timestampId = React.useId();
  const textareaId = React.useId();

  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-hairline bg-card/60 p-4" aria-busy>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mt-3 h-40 w-full" />
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-hairline bg-card/60 p-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-[14px] text-ink-1">
            <Trans>Contesto di business</Trans>
          </h3>
          <p id={timestampId} className="mt-0.5 text-[11.5px] text-ink-3">
            <Trans>Ultimo aggiornamento {formatTimestamp(savedAt) ?? i18n._(msg`Mai aggiornato`)}</Trans>
            {serverText.length > 0 ? (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-accent/10 px-1.5 py-0.5 font-medium text-[10px] text-accent">
                <Sparkles className="h-2.5 w-2.5" aria-hidden />
                <Trans>AI generato</Trans>
              </span>
            ) : null}
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-1">
        <label htmlFor={textareaId} className="sr-only">
          <Trans>Testo del contesto di business</Trans>
        </label>
        <textarea
          id={textareaId}
          aria-describedby={timestampId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          placeholder={i18n._(msg`Descrivi i processi di business, il significato delle entità principali e i KPI usati per interrogare i dati…`)}
          disabled={updateMutation.isPending || regenerateMutation.isPending}
          className="w-full resize-y rounded-md border border-hairline bg-white/60 px-3 py-2 text-[12.5px] text-ink-1 leading-relaxed placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:bg-ink-3/[0.04]"
        />
      </div>

      {regenerateMutation.isPending ? (
        <p className="text-[11.5px] text-ink-3"><Trans>Generazione in corso, attendi…</Trans></p>
      ) : null}

      <InlineErrorBanner message={error} />

      <footer className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={!isDirty || updateMutation.isPending || regenerateMutation.isPending}
        >
          <Trans>Annulla</Trans>
        </Button>

        <AlertDialog open={confirmRegenerate} onOpenChange={setConfirmRegenerate}>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={regenerateMutation.isPending || updateMutation.isPending}
            >
              {regenerateMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3 w-3" aria-hidden />
              )}
              <Trans>Rigenera con AI</Trans>
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Rigenerare il contesto?</Trans></AlertDialogTitle>
              <AlertDialogDescription>
                <Trans>
                  Eventuali modifiche manuali verranno sovrascritte dal testo generato dall'AI.
                  L'operazione richiede 10-30 secondi.
                </Trans>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel><Trans>Annulla</Trans></AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmRegenerate(false);
                  handleRegenerate();
                }}
              >
                <Trans>Rigenera</Trans>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || updateMutation.isPending || regenerateMutation.isPending}
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : null}
          <Trans>Salva</Trans>
        </Button>
      </footer>
    </section>
  );
};
BusinessContextEditor.displayName = "BusinessContextEditor";
